/**
 * TenantDetailDialog
 *
 * 테넌트 카드의 "상세" 버튼에서 열리는 풀스크린 다이얼로그.
 * 탭: 대화 / 규칙 (후속: Workspace, Admin).
 */

import { useState } from "react";
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import DescriptionIcon from "@mui/icons-material/Description";
import StorageIcon from "@mui/icons-material/Storage";

import { ConversationViewer } from "./ConversationViewer";
import { RulesEditor } from "./RulesEditor";
import { BucketBrowser } from "./BucketBrowser";

type TabValue = "conversations" | "rules" | "bucket";

interface TenantDetailDialogProps {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  tenantName: string;
  emoji: string;
}

export function TenantDetailDialog({
  open,
  onClose,
  tenantCode,
  tenantName,
  emoji,
}: TenantDetailDialogProps) {
  const [tab, setTab] = useState<TabValue>("conversations");

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar position="sticky" color="default" elevation={0}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {emoji} {tenantName}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontFamily: "monospace" }}
            >
              {tenantCode}
            </Typography>
          </Typography>
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
        <Tabs
          value={tab}
          onChange={(_e, v: TabValue) => setTab(v)}
          sx={{ px: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<ChatIcon fontSize="small" />}
            iconPosition="start"
            value="conversations"
            label="대화"
          />
          <Tab
            icon={<DescriptionIcon fontSize="small" />}
            iconPosition="start"
            value="rules"
            label="규칙"
          />
          <Tab
            icon={<StorageIcon fontSize="small" />}
            iconPosition="start"
            value="bucket"
            label="GCS 버킷"
          />
        </Tabs>
      </AppBar>

      <Box sx={{ flex: 1, overflow: "hidden", p: 2, height: "calc(100vh - 112px)" }}>
        {tab === "conversations" && <ConversationViewer tenantCode={tenantCode} />}
        {tab === "rules" && <RulesEditor tenantCode={tenantCode} />}
        {tab === "bucket" && <BucketBrowser tenantCode={tenantCode} />}
      </Box>
    </Dialog>
  );
}
